import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCreateDesk, useDesks, usePrefetchDesk } from "@/api/hooks";
import { DeskRow, DeskRowSkeleton, TestDeskRow } from "@/components/desk-row";
import { UserMenu } from "@/components/user-menu";

export function Dashboard() {
  const { data, isLoading, error } = useDesks();
  const createDesk = useCreateDesk();
  const navigate = useNavigate();
  const prefetchDesk = usePrefetchDesk();

  const isDebug =
    typeof window !== "undefined" && window.location.search.includes("debug");
  const [showBorders, setShowBorders] = useState(false);
  useEffect(() => {
    if (isDebug && showBorders) document.documentElement.classList.add("debug");
    else document.documentElement.classList.remove("debug");
  }, [isDebug, showBorders]);

  const [searchQuery, setSearchQuery] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [expandedDeskId, setExpandedDeskId] = useState<string | null>(null);

  const desks = data?.desks ?? [];
  const hasSearch = searchQuery.trim().length > 0;
  const filtered = hasSearch
    ? desks.filter((d) =>
        d.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : desks;

  const handleCreate = async () => {
    const name = searchQuery.trim();
    if (!name) return;
    try {
      const result = await createDesk.mutateAsync({ name });
      setSearchQuery("");
      setDroppedFiles([]);
      navigate({ to: "/desk/$deskId", params: { deskId: result.desk.id } });
    } catch {}
  };

  const handleOpen = useCallback(
    (deskId: string, _sessionId?: string) => {
      navigate({ to: "/desk/$deskId", params: { deskId } });
    },
    [navigate],
  );

  const handleToggle = useCallback((deskId: string) => {
    setExpandedDeskId((prev) => (prev === deskId ? null : deskId));
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) setDroppedFiles((prev) => [...prev, ...files]);
  }, []);

  const removeFile = (index: number) => {
    setDroppedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (
        e.key === "/" &&
        !e.metaKey &&
        !e.ctrlKey &&
        document.activeElement?.tagName !== "INPUT"
      ) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape") {
        if (document.activeElement === searchRef.current) {
          searchRef.current?.blur();
          setSearchQuery("");
        } else if (expandedDeskId) {
          setExpandedDeskId(null);
        }
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [expandedDeskId]);

  return (
    <div className="h-full flex flex-col">
      <div
        style={{
          position: "fixed",
          top: 14,
          right: 16,
          zIndex: 40,
        }}
      >
        <UserMenu />
      </div>

      {isDebug && (
        <button
          type="button"
          onClick={() => setShowBorders((b) => !b)}
          style={{
            position: "fixed",
            top: 8,
            right: 8,
            zIndex: 9999,
            padding: "3px 10px",
            borderRadius: "999px",
            border: "1px solid var(--border)",
            background: showBorders ? "var(--t1)" : "var(--surface)",
            color: showBorders ? "var(--surface)" : "var(--t4)",
            fontSize: "10px",
            fontFamily: "var(--font-mono)",
            cursor: "pointer",
            opacity: 0.8,
          }}
        >
          {showBorders ? "debug on" : "debug off"}
        </button>
      )}

      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="text-center py-16 text-[var(--err)] text-[14px]">
            Cannot connect to orchestrator
          </div>
        )}

        {isLoading && (
          <>
            <DeskRowSkeleton />
            <DeskRowSkeleton />
            <DeskRowSkeleton />
          </>
        )}

        {!isLoading && !error && filtered.length === 0 && !hasSearch && (
          <div
            className="flex flex-col items-center justify-center py-20 text-[var(--t4)]"
            style={{ animation: "fadeInUp 0.4s ease" }}
          >
            <img
              src="/frogs/sleeping.gif"
              alt="Sleeping frog waiting for work"
              className="w-16 h-16 mb-4 opacity-60"
            />
            <p
              className="text-[22px] font-bold mb-2"
              style={{ fontFamily: "var(--font-display)", color: "var(--t2)" }}
            >
              Your workshop awaits
            </p>
            <p className="text-[14px]">
              Name a desk below to get started — or press{" "}
              <kbd
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "11px",
                  padding: "1px 5px",
                  borderRadius: "3px",
                  border: "1px solid var(--border)",
                  background: "var(--surface)",
                }}
              >
                /
              </kbd>{" "}
              to jump here
            </p>
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && hasSearch && (
          <div className="flex flex-col items-center justify-center py-16 text-[var(--t4)]">
            <p className="text-[15px]">
              No desks match "
              <span className="text-[var(--t2)]">{searchQuery}</span>"
            </p>
            <button
              type="button"
              onClick={handleCreate}
              className="mt-3 text-[14px] text-[var(--accent)] hover:underline font-medium"
            >
              Create a desk called "{searchQuery}"
            </button>
          </div>
        )}

        {isDebug && (
          <TestDeskRow
            expanded={expandedDeskId === "__test__"}
            onToggle={() => handleToggle("__test__")}
            onOpen={handleOpen}
          />
        )}

        {filtered.map((desk, i) => (
          <DeskRow
            key={desk.id}
            desk={desk}
            index={i}
            expanded={expandedDeskId === desk.id}
            onToggle={() => handleToggle(desk.id)}
            onOpen={handleOpen}
            onPrefetch={prefetchDesk}
          />
        ))}

        <div className="h-20" />
      </div>

      {/* Bottom bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--surface)] border-t border-[var(--border)] transition-all"
        style={{
          padding:
            isDragging || droppedFiles.length > 0 ? "16px 32px" : "12px 32px",
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="mb-3 py-4 border-2 border-dashed border-[var(--accent)] rounded-lg bg-[var(--accent-light)] flex items-center justify-center text-[var(--accent)] text-[14px] font-medium">
            Drop files to include with your new desk
          </div>
        )}

        {droppedFiles.length > 0 && !isDragging && (
          <div className="mb-3 flex gap-2 flex-wrap">
            {droppedFiles.map((f, i) => (
              <span
                key={`drop-${f.name}-${i}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[var(--hover)] text-[12px] text-[var(--t2)]"
              >
                <span style={{ fontFamily: "var(--font-mono)" }}>{f.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-[var(--t4)] hover:text-[var(--err)] ml-1"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <div className="search-focus-ring flex-1 flex items-center gap-2 bg-[var(--white)] border border-[var(--border)] rounded px-3 py-2.5">
            <svg
              className="text-[var(--t4)] flex-shrink-0"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && hasSearch) handleCreate();
              }}
              placeholder="Search desks or type a name to create..."
              className="flex-1 bg-transparent border-none outline-none text-[14px] text-[var(--t1)] placeholder:text-[var(--t4)]"
              style={{ fontFamily: "var(--font-ui)" }}
            />
          </div>
          {hasSearch && (
            <button
              type="button"
              onClick={handleCreate}
              disabled={createDesk.isPending}
              className="flex items-center gap-2 px-4 h-[42px] bg-[var(--accent)] text-white border-none rounded cursor-pointer hover:bg-[var(--accent-dark)] transition-colors text-[13px] font-medium disabled:opacity-50 whitespace-nowrap"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {createDesk.isPending ? "Creating..." : "New Desk"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
